import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardShell, { StatCard } from "@/components/DashboardShell";
import { api, fmtNGN, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ArrowRight, Bank, Receipt, CheckCircle, CircleNotch, Warning } from "@phosphor-icons/react";

function firstName(nameOrEmail) {
  if (!nameOrEmail) return "there";
  const first = nameOrEmail.split(/[ .@]/)[0];
  return first ? first.charAt(0).toUpperCase() + first.slice(1) : "there";
}

export default function ParentDashboard() {
  const { user } = useAuth();
  const [loans, setLoans] = useState([]);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    api.get("/loans/mine").then((r) => setLoans(r.data)).catch(() => {});
    api.get("/payments/direct-pay/history/list")
      .then((r) => setHistory(r.data))
      .catch(() => {})
      .finally(() => setLoadingHistory(false));
  }, []);

  const totalPaid = history.filter((h) => h.status === "paid").reduce((s, h) => s + (h.amount || 0), 0);

  return (
    <DashboardShell title={`Hello, ${firstName(user?.name || user?.email)}`} subtitle="Pay a school directly, apply for a school-fee loan, or get an Academic Liquidity Advance.">
      {/* Hero — direct pay CTA */}
      <div className="card !p-8 grain bg-gradient-to-br from-[#0A3622] to-[#072618] text-white border-0 mb-8" data-testid="parent-pay-hero">
        <div className="overline !text-[#F2B632]">Pay school fees</div>
        <div className="font-display text-4xl md:text-5xl font-medium mt-3 tracking-tight leading-tight max-w-2xl">
          Pay a school directly, from your card into their bank account.
        </div>
        <p className="text-white/70 mt-3 text-sm max-w-xl">
          Enter the school's account, verify the name, pay via Paystack. The school is credited by the next working day.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link to="/parent/pay" className="btn-primary !bg-[#F2B632]" data-testid="pay-school-cta">
            Pay a school <ArrowRight size={16} weight="bold" />
          </Link>
          <Link to="/parent/loans" className="btn-outline !text-white !border-white/40 hover:!bg-white hover:!text-[#0A3622]" data-testid="apply-loan-cta">
            Apply for a loan
          </Link>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-8">
        <StatCard label="Paid this year" value={fmtNGN(totalPaid)} sub={`${history.filter((h) => h.status === "paid").length} successful payments`} accent="primary" />
        <StatCard label="Active loans" value={loans.filter((l) => l.state === "disbursed").length} sub={`${loans.length} total applications`} accent="accent" />
      </div>

      {/* Payment history */}
      <div className="card mb-8">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-medium">Recent payments</h2>
          <Link to="/parent/pay" className="text-sm text-[#F2B632] hover:underline" data-testid="new-payment-link">
            Make a payment <ArrowRight size={12} className="inline" />
          </Link>
        </div>
        {loadingHistory ? (
          <div className="py-8 text-center text-sm text-[#4F5D4F]"><CircleNotch className="animate-spin inline mr-2" size={14} /> Loading…</div>
        ) : history.length === 0 ? (
          <div className="py-10 text-center">
            <Bank size={32} weight="duotone" className="text-[#4F5D4F] mx-auto mb-3" />
            <p className="text-sm text-[#4F5D4F]">No payments yet.</p>
            <Link to="/parent/pay" className="btn-ghost text-sm mt-2 inline-flex" data-testid="first-payment-cta">
              Make your first payment <ArrowRight size={12} weight="bold" />
            </Link>
          </div>
        ) : (
          <table className="dense">
            <thead><tr><th>Date</th><th>School</th><th>Child</th><th>Amount</th><th>Reference</th><th>Status</th></tr></thead>
            <tbody>
              {history.slice(0, 8).map((h) => (
                <tr key={h.id} data-testid={`payment-row-${h.id}`}>
                  <td>{new Date(h.created_at).toLocaleDateString()}</td>
                  <td className="truncate max-w-[160px]">{h.school_name}</td>
                  <td className="truncate max-w-[140px]">{h.child_name}</td>
                  <td className="font-mono">{fmtNGN(h.amount)}</td>
                  <td className="font-mono text-xs text-[#4F5D4F]">{h.reference}</td>
                  <td><PayStatus status={h.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Loans summary */}
      <div className="card">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-display text-xl font-medium">Recent loan applications</h2>
          <Link to="/parent/loans" className="text-sm text-[#F2B632] hover:underline" data-testid="view-all-loans">View all <ArrowRight size={12} className="inline" /></Link>
        </div>
        {loans.length === 0 ? (
          <p className="text-sm text-[#4F5D4F] py-6 text-center">No loans yet. Apply for one to ease term-fee pressure.</p>
        ) : (
          <table className="dense">
            <thead><tr><th>Date</th><th>Amount</th><th>Tenor</th><th>Status</th></tr></thead>
            <tbody>
              {loans.slice(0, 5).map((l) => (
                <tr key={l.id} data-testid={`loan-row-${l.id}`}>
                  <td>{new Date(l.created_at).toLocaleDateString()}</td>
                  <td className="font-mono">{fmtNGN(l.amount)}</td>
                  <td>{l.tenor_months} mo</td>
                  <td><LoanStatus status={l.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </DashboardShell>
  );
}

function PayStatus({ status }) {
  const map = {
    paid: ["badge-success", "Settled"],
    charged: ["badge-warning", "Settling"],
    settling: ["badge-warning", "Settling"],
    pending: ["badge-neutral", "Pending"],
    failed: ["badge-danger", "Failed"],
  };
  const [c, l] = map[status] || ["badge-neutral", status || "—"];
  return <span className={`badge ${c}`}>{l}</span>;
}

function LoanStatus({ status }) {
  const map = {
    approved: ["badge-success", "Approved"],
    pending: ["badge-warning", "Pending review"],
    declined: ["badge-danger", "Declined"],
    default: ["badge-danger", "In default"],
  };
  const [c, l] = map[status] || ["badge-neutral", status];
  return <span className={`badge ${c}`}>{l}</span>;
}
